import { queryOptions } from '@tanstack/react-query';
import { BrandMediaService } from '@inference.engine/guidelines-api-client/web';
import { guidelinesHttp } from '@/lib/api/guidelines-client';
import { describeApiFailure } from '@/shared/api/describeApiFailure';
import type { ListBrandMediaQuery } from '@/shared/lib/brand-media';
import { BRAND_MEDIA_QUERY_KEYS } from './queries';

export interface MediaFacet {
  key: string;
  values: Array<{ value: string; count: number }>;
}

/**
 * Which tags the scoped media carries.
 *
 * Keyed on the whole question, like the listing, so changing the folder or a tag refetches. No
 * staleTime: an upload or a tag edit changes what this answers, and a rail showing counts from before
 * somebody's own edit reads as the edit having failed.
 */
export const mediaFacetsQueryOptions = (query: ListBrandMediaQuery) =>
  queryOptions({
    queryKey: [...BRAND_MEDIA_QUERY_KEYS.all, 'facets', query] as const,
    queryFn: async (): Promise<MediaFacet[]> => {
      const response = await BrandMediaService.facets({ client: guidelinesHttp, query });
      if (!response.data) {
        throw new Error(describeApiFailure(response.error, 'The tags could not be loaded.'));
      }
      return (response.data as unknown as { facets: MediaFacet[] }).facets;
    },
  });

/**
 * A page-independent stand-in for how many rows the current scope holds.
 *
 * The grid caps at a page size, so counting its tiles undercounts a folder larger than one page and
 * makes every facet look narrower than it really is. Facet counts, by contrast, are aggregated by
 * the server over the whole scope, not just the page rendered, so they stay a sound total past that
 * cap.
 *
 * No single key is guaranteed to cover every row - one nothing carries would sum to less than the
 * true total - so this takes the highest per-key sum across all of them as the closest available
 * stand-in. Understating the total is the failure to avoid: it is what would make some other facet's
 * count look, wrongly, like it covers everything in scope, dimming a facet that could still narrow.
 * The highest sum available is the least likely of the candidates to make that mistake.
 *
 * The rows on the page join the candidates for the same reason, and they are what saves a library
 * where tags are rare: with one key on a handful of assets, every sum here is that handful, and the
 * only facet there is would read as covering everything while it in fact narrows the library to one
 * asset. The page cannot overstate the scope, so it is safe to take whichever is larger.
 */
export const totalItemsInScope = (facets: readonly MediaFacet[], rowsOnPage = 0): number =>
  Math.max(
    0,
    rowsOnPage,
    ...facets.map((facet) => facet.values.reduce((sum, entry) => sum + entry.count, 0)),
  );
